import * as React from 'react'

import { ChevronDown } from 'lucide-react'

import { cn } from '@/shared/lib'

type AccordionType = 'single' | 'multiple'
type AccordionValue = string | string[]

interface AccordionContextValue {
  openItems: string[]
  handleValueChange: (value: string) => void
  type: AccordionType
}

const AccordionContext = React.createContext<AccordionContextValue | undefined>(undefined)

interface AccordionItemContextValue {
  value: string
}

const AccordionItemContext = React.createContext<AccordionItemContextValue | undefined>(undefined)

function useAccordionContext(): AccordionContextValue {
  const context = React.useContext(AccordionContext)
  if (!context) {
    throw new Error('Accordion components must be used within an Accordion')
  }
  return context
}

function useAccordionItemContext(): AccordionItemContextValue {
  const context = React.useContext(AccordionItemContext)
  if (!context) {
    throw new Error('AccordionTrigger/AccordionContent must be used within an AccordionItem')
  }
  return context
}

function normalizeValues(value: AccordionValue | undefined): string[] {
  if (!value) return []
  if (Array.isArray(value)) return value
  return value.split(' ').filter(Boolean)
}

interface AccordionProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: AccordionType
  collapsible?: boolean
  value?: AccordionValue
  defaultValue?: AccordionValue
  onValueChange?: (value: AccordionValue) => void
}

interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string
}

function Accordion({
  className,
  children,
  type = 'single',
  collapsible = false,
  value,
  defaultValue,
  onValueChange,
  ...props
}: Readonly<AccordionProps>) {
  const [openItems, setOpenItems] = React.useState<string[]>(
    value !== undefined ? normalizeValues(value) : normalizeValues(defaultValue),
  )

  React.useEffect(() => {
    if (value !== undefined) {
      setOpenItems(normalizeValues(value))
    }
  }, [value])

  const handleValueChange = React.useCallback(
    (itemValue: string) => {
      if (type === 'single') {
        const newValue = openItems.includes(itemValue) && collapsible ? [] : [itemValue]
        setOpenItems(newValue)

        if (onValueChange) {
          const singleValue: string = newValue?.[0] ?? ''
          onValueChange(singleValue)
        }
      } else {
        const newOpenItems = openItems.includes(itemValue)
          ? openItems.filter((i) => i !== itemValue)
          : [...openItems, itemValue]
        setOpenItems(newOpenItems)

        if (onValueChange) {
          onValueChange(newOpenItems)
        }
      }
    },
    [type, collapsible, openItems, onValueChange],
  )

  const values = React.useMemo(
    () => ({
      openItems,
      handleValueChange,
      type,
    }),
    [handleValueChange, openItems, type],
  )

  return (
    <AccordionContext.Provider value={values}>
      <div className={cn('w-full', className)} data-slot="accordion" {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  )
}

function AccordionItem({ className, value, children, ...props }: Readonly<AccordionItemProps>) {
  const values = React.useMemo(
    () => ({
      value,
    }),
    [value],
  )
  return (
    <AccordionItemContext.Provider value={values}>
      <div data-testid="accordion-item" data-slot="accordion-item" className={cn('border-b', className)} {...props}>
        {children}
      </div>
    </AccordionItemContext.Provider>
  )
}

function AccordionHeader({ className, children, ...props }: Readonly<React.ComponentProps<'div'>>) {
  return (
    <div className={cn('flex', className)} {...props}>
      {children}
    </div>
  )
}

function AccordionTrigger({ className, children, ...props }: Readonly<React.ComponentProps<'button'>>) {
  const { value } = useAccordionItemContext()
  const { openItems, handleValueChange } = useAccordionContext()
  const isOpen = openItems.includes(value)

  return (
    <AccordionHeader>
      <button
        type="button"
        data-slot="accordion-trigger"
        data-state={isOpen ? 'open' : 'closed'}
        className={cn(
          'focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 cursor-pointer items-center justify-between gap-1 rounded-md py-4 text-left text-sm font-semibold transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 sm:gap-4',
          className,
        )}
        onClick={() => handleValueChange(value)}
        aria-expanded={isOpen}
        {...props}
      >
        {children}
        <ChevronDown
          className={cn(
            'pointer-events-none size-8 shrink-0 text-neutral-800 transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>
    </AccordionHeader>
  )
}

function AccordionContent({ className, children, ...props }: Readonly<React.ComponentProps<'div'>>) {
  const { value } = useAccordionItemContext()
  const { openItems } = useAccordionContext()
  const isOpen = openItems.includes(value)

  const [shouldRender, setShouldRender] = React.useState(isOpen)

  const animationTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  React.useEffect(() => {
    if (isOpen) {
      setShouldRender(true)

      if (animationTimeout.current) {
        clearTimeout(animationTimeout.current)
        animationTimeout.current = null
      }
    } else {
      animationTimeout.current = setTimeout(() => {
        setShouldRender(false)
      }, 100)
    }

    return () => {
      if (animationTimeout.current) {
        clearTimeout(animationTimeout.current)
        animationTimeout.current = null
      }
    }
  }, [isOpen])

  if (!shouldRender) {
    return null
  }

  return (
    <div
      className={cn(
        'overflow-hidden pt-0 pb-4 transition-all duration-100 ease-out',
        isOpen ? 'transform-none opacity-100' : 'translate-y-[-8px] transform opacity-0',
        className,
      )}
      data-slot="accordion-content"
      data-state={isOpen ? 'open' : 'closed'}
      {...props}
    >
      {children}
    </div>
  )
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger }
